import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { t } from "i18next";
import CustomBreadCrumb from "../../../components/CustomBreadCrumb";
import { TOAST_TYPE, ACTIONS } from "../../../constants/Constants";
import toastContext from "../../../context/toast/ToastContext";
import themeContext from "../../../context/theme/ThemeContext";
import userContext from "../../../context/users/UserContext";
import Select from "react-select";
import { constantStyleForSelect } from "../../../components/CustomSelect";
import {
  addOrUpdateStyle,
  getStylesList,
  getLLMAgentList,
} from "../../../service/MasterService";
import loaderContext from "../../../context/loader/LoaderContext";

const customStyles = `
  .style-content-textarea {
    background-color: #ffffff !important;
    color: #000000 !important;
    width: 96.3% !important;
  }

  .style-content-textarea:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: inherit !important;
  }
`;

const AddStyle = ({ action = ACTIONS.ADD }) => {
  const { styleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getThemeColor, getAlterColor, themeName } = useContext(themeContext);
  const { loadByUsername, authenticated } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const isLightMode = themeName === "Light" || themeName === "light";
  const alterBg = getAlterColor ? getAlterColor("background-color") : null;
  const themeBg = getThemeColor("background");
  const mainBackgroundColor = isLightMode
    ? "#ffffff"
    : alterBg || themeBg || "#000000";

  const isEdit =
    action === ACTIONS.EDIT || location.pathname.includes("/edit/");
  const [isLoading, setIsLoading] = useState(true);
  const [reportTemplateOptions, setReportTemplateOptions] = useState([]);
  const [stylesData, setStylesData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [promptContent, setpromptContent] = useState("");
  const [breadCrumbItems] = useState([
    {
      text: t("navbar.agentic-style"),
      key: "my_styles",
      onClick: () => (window.location.href = "/my-style/1"),
    },
    {
      text: isEdit ? t("addStyle.editStyle") : t("addStyle.addStyle"),
      key: isEdit ? "edit_style" : "add_style",
    },
  ]);

  useEffect(() => {
    if (!authenticated) {
      loadByUsername();
    }
  }, [authenticated]);

  useEffect(() => {
    const fetchData = async () => {
      setLoaderRunning(true);
      ref.current.continuousStart();

      try {
        const [stylesResponse, agentsResponse] = await Promise.all([
          getStylesList(isEdit ? 1 : 0),
          getLLMAgentList(),
        ]);

        let options = [];

        if (agentsResponse?.data?.data) {
          options = agentsResponse.data.data
            .map((agent) => ({
              value: agent.id,
              label: agent.title,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          setReportTemplateOptions(options);
        }

        if (
          stylesResponse?.status === 200 &&
          stylesResponse?.data?.result === "SUCCESS"
        ) {
          setStylesData(stylesResponse.data.data);

          if (isEdit && styleId) {
            const style = stylesResponse.data.data.styles.find(
              (s) => s.id === parseInt(styleId),
            );

            if (style) {
              const matchedOption =
                options.find((option) => option.value === style.agentId) ||
                options.find((option) => option.label === style.title);

              if (matchedOption) {
                setSelectedTemplate(matchedOption);
              } else if (style.title) {
                setSelectedTemplate({
                  value: style.agentId || "",
                  label: style.title,
                });
              }

              if (style.styles) {
                setpromptContent(style.styles);
              }
            } else {
              showToastv2(TOAST_TYPE.ERROR, t("addStyle.styleNotFound"));
              navigate("/my-style/1");
            }
          }
        } else {
          showToastv2(TOAST_TYPE.ERROR, t("addStyle.fetchFailed"));
          navigate("/my-style/1");
        }
      } catch (error) {
        showToastv2(TOAST_TYPE.ERROR, t("addStyle.fetchError"));
        navigate("/my-style/1");
      } finally {
        setIsLoading(false);
        setLoaderRunning(false);
        ref.current.complete();
      }
    };

    fetchData();
  }, [styleId, isEdit]);

  useEffect(() => {
    if (isLoading) {
      setLoaderRunning(true);
      ref.current.continuousStart();
    } else {
      setLoaderRunning(false);
      ref.current.complete();
    }
    return () => {
      setLoaderRunning(false);
      ref.current.complete();
    };
  }, [isLoading]);

  const handleSubmit = async () => {
    const trimmedPrompt = promptContent?.trim();
    if (!selectedTemplate) {
      showToastv2(TOAST_TYPE.ERROR, t("addStyle.selectTemplate"));
      return;
    }
    if (!trimmedPrompt) {
      showToastv2(TOAST_TYPE.ERROR, t("addStyle.fillPrompt"));
      return;
    }

    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
      if (
        !isEdit &&
        stylesData?.styles?.some(
          (s) =>
            s.agentId === selectedTemplate.value ||
            (!s.agentId && s.title === selectedTemplate.label),
        )
      ) {
        showToastv2(
          TOAST_TYPE.ERROR,
          `A prompt for "${selectedTemplate.label}" already exists. Please edit it instead.`,
        );
        return;
      }

      const payload = {
        ...(isEdit && styleId && { id: Number(styleId) }),
        agentId: selectedTemplate.value,
        ...(trimmedPrompt && { styles: trimmedPrompt }),
      };

      console.log("Submitting payload\n", JSON.stringify(payload, null, 2));

      const response = await addOrUpdateStyle(payload);

      if (
        response?.data?.result === "SUCCESS" ||
        [200, 201].includes(response?.status)
      ) {
        showToastv2(
          TOAST_TYPE.SUCCESS,
          response.data?.message ??
          (isEdit
            ? t("addStyle.updatedSuccess")
            : t("addStyle.createdSuccess")),
        );
        setTimeout(() => (window.location.href = "/my-style/1"), 500);
      } else {
        showToastv2(
          TOAST_TYPE.ERROR,
          response.data?.message ??
          `Failed to ${isEdit ? "update" : "add"} prompt`,
        );
      }
    } catch (err) {
      showToastv2(
        TOAST_TYPE.ERROR,
        `Error ${isEdit ? "updating" : "adding"} prompt`,
      );
      console.error(err);
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  if (isLoading) {
    return (
      <div
        className="main-container-body"
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <div className="breadCrumb">
          <CustomBreadCrumb items={breadCrumbItems} />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            backgroundColor: mainBackgroundColor,
          }}
        ></div>
      </div>
    );
  }

  return (
    <div
      className="main-container-body"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: mainBackgroundColor,
      }}
    >
      <div className="breadCrumb">
        <CustomBreadCrumb items={breadCrumbItems} />
      </div>
      <div
        className="report-template-content modal-scroll"
        style={{ flex: 1, overflowY: "auto", padding: "10px 20px" }}
      >
        <div className="report-template-form" style={{ width: "70%" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: mainBackgroundColor,
              borderRadius: "4px",
            }}
          >
            <div style={{ width: "100%", padding: "10px 0px 0px 20px" }}>
              <section className="sectionA">
                <p
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginLeft: "10px",
                  }}
                >
                  {t("addStyle.styleDetails")}
                </p>
              </section>

              <div className="ms-Grid" dir="ltr">
                <div className="ms-Grid-row">
                  <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
                    <div
                      className="agentic-report-template-list-key"
                      style={{ marginBottom: "10px" }}
                    >
                      {t("addStyle.reportingAgents")}
                      <span
                        style={{
                          marginLeft: "4px",
                          color: "red",
                          fontWeight: "bold",
                        }}
                      >
                        *
                      </span>
                    </div>
                    <Select
                      classNamePrefix="react-select"
                      options={reportTemplateOptions}
                      value={selectedTemplate}
                      styles={constantStyleForSelect(getThemeColor)}
                      onChange={setSelectedTemplate}
                      isDisabled={isEdit}
                    />
                  </div>
                </div>
              </div>

              <div style={{ padding: "10px", marginTop: "20px" }}>
                <div
                  className="agentic-report-template-list-key"
                  style={{ marginBottom: "10px" }}
                >
                  {t("addStyle.myPrompts")}
                  <span
                    style={{
                      marginLeft: "4px",
                      color: "red",
                      fontWeight: "bold",
                    }}
                  >
                    *
                  </span>
                </div>
                <textarea
                  id="style-content"
                  name="style-content"
                  className="style-content-textarea"
                  value={promptContent}
                  onChange={(e) => setpromptContent(e.target.value)}
                  placeholder={t("addStyle.placeholder")}
                  style={{
                    height: "100px",
                    padding: "15px",
                    border: getThemeColor("border"),
                    borderRadius: "10px",
                    fontFamily: "Segoe UI",
                    fontSize: "14px",
                    resize: "none",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="bottom-bar"
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "flex-start",
          gap: "10px",
          marginLeft: "40px",
          borderTop: `1px solid ${getThemeColor("border")}`,
        }}
      >
        <DefaultButton
          text={t("button.cancel")}
          onClick={() => navigate("/my-style/1")}
          styles={{
            root: { border: getThemeColor("border"), borderRadius: "7px" },
          }}
        />
        <PrimaryButton
          text={t("button.save")}
          onClick={handleSubmit}
          styles={{ root: { borderRadius: "7px" } }}
        />
      </div>
      <style>{customStyles}</style>
    </div>
  );
};

export default AddStyle;
